import React from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import cloneDeep from 'lodash.clonedeep';
import classNames from 'classnames';
import SubmitDialog from '../submit-dialog';

function withPrompt(ModalComponent) {
  class PromptProvider extends React.Component {
    constructor(props) {
      super(props);

      this.state = {
        showPrompt: false,
      };

      this.showPrompt = this.showPrompt.bind(this);
      this.hidePrompt = this.hidePrompt.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onPressEsc = this.onPressEsc.bind(this);

      const { data } = this.props;
      this.initialValues = cloneDeep(data);
    }

    onPressEsc() {
      const { overlay, handleCancel } = this.props;

      if (overlay === 'visible') {
        this.handlePrompt();
        return;
      }
      handleCancel();
    }

    onOverlayClick() {
      const { overlay, onOverlayClick, handleSubmit } = this.props;

      if (onOverlayClick !== Function.prototype) {
        onOverlayClick();
        return;
      }

      if (overlay === 'visible') {
        this.handlePrompt();
        return;
      }

      handleSubmit();
    }

    handlePrompt() {
      const { data: currentData, handleCancel, showPromptForce } = this.props;
      if (showPromptForce || !isEqual(this.initialValues, currentData)) {
        this.showPrompt();
        return;
      }

      handleCancel();
    }

    showPrompt() {
      this.setState({ showPrompt: true });
    }

    hidePrompt() {
      this.setState({ showPrompt: false });
    }

    render() {
      const { showPrompt } = this.state;
      const {
        handleCancel,
        handleSubmit,
        children,
        promptCaptions,
        data,
        isValid,
        modalRef,
        overlay,
        containerClassName,
        ...otherProps
      } = this.props;

      return (
        <React.Fragment>
          <ModalComponent
            {...otherProps}
            ref={modalRef}
            overlay={overlay}
            containerClassName={classNames(containerClassName, {
              'ws-modal-container--transparent': showPrompt,
            })}
            onOverlayClick={this.onOverlayClick}
            onPressEsc={this.onPressEsc}
            shouldDisablePointerEvents={!isValid}
          >
            {children}
          </ModalComponent>
          {showPrompt && (
            <SubmitDialog
              onDiscard={() => {
                this.hidePrompt();
                handleCancel();
              }}
              onSubmit={() => {
                this.hidePrompt();
                handleSubmit();
              }}
              onCancel={() => {
                this.hidePrompt();
              }}
              captions={promptCaptions}
            />
          )}
        </React.Fragment>
      );
    }
  }

  PromptProvider.propTypes = {
    handleCancel: PropTypes.func,
    handleSubmit: PropTypes.func,
    onOverlayClick: PropTypes.func,
    modalRef: PropTypes.shape(),
    data: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
    children: PropTypes.node.isRequired,
    isValid: PropTypes.bool,
    showPromptForce: PropTypes.bool,
    promptCaptions: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      discard: PropTypes.string,
      submit: PropTypes.string.isRequired,
      cancel: PropTypes.string.isRequired,
    }),
    containerClassName: PropTypes.string,
    overlay: PropTypes.oneOf(['transparent', 'visible', 'none']),
  };

  PromptProvider.defaultProps = {
    data: null,
    handleCancel: Function.prototype,
    handleSubmit: Function.prototype,
    onOverlayClick: Function.prototype,
    modalRef: null,
    isValid: true,
    showPromptForce: false,
    overlay: 'transparent',
    containerClassName: '',
    promptCaptions: {
      title: 'Are you sure?',
      description: 'Really?',
      discard: 'Discard',
      submit: 'Submit',
      cancel: 'Cancel',
    },
  };

  return PromptProvider;
}

export default withPrompt;
